import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import '../styles/Register.css';
import { COLORS } from '../styles/colors';
import Dialog from '../components/Dialog';
import logoImage from '../assets/tapgo-logo.png';
import { 
  sendVerificationSMS, 
  verifyVerificationCode,
  getPublicCompanyInfo,
  getCompanyContract,
  registerUserCommon
} from '../utils/api';

const UserRegister = () => {
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [showDialog, setShowDialog] = useState(false);
  const [dialogMessage, setDialogMessage] = useState('');
  const [registrationSuccess, setRegistrationSuccess] = useState(false);
  
  // 인증번호 관련 상태
  const [isVerifySent, setIsVerifySent] = useState(false);
  const [isVerified, setIsVerified] = useState(false);
  const [verificationCode, setVerificationCode] = useState('');
  
  // 폼 데이터 상태 관리
  const [formData, setFormData] = useState({
    email: '',
    name: '',
    password: '',
    passwordConfirm: '',
    phoneNumber: '',
    verificationCode: '',
    companyCode: '',
    birthdate: '',
  });

  // 유효성 검사 오류 메시지 상태
  const [validationErrors, setValidationErrors] = useState({
    password: '',
    passwordConfirm: '',
    phoneNumber: '',
    birthdate: ''
  });
  
  // 약관 동의 상태
  const [agreements, setAgreements] = useState({
    all: false,
    service: false,
    privacy: false
  });
  
  // 비밀번호 유효성 검사 함수
  const validatePassword = (password) => {
    // 특수문자와 숫자를 포함한 8자리 이상 비밀번호 검증
    const passwordRegex = /^(?=.*[0-9])(?=.*[!@#$%^&*])[a-zA-Z0-9!@#$%^&*]{8,}$/;
    return passwordRegex.test(password);
  };
  
  // 전화번호 유효성 검사 함수
  const validatePhoneNumber = (phoneNumber) => {
    // 하이픈(-) 없이 11자리 숫자인지 검증
    const phoneRegex = /^[0-9]{11}$/;
    return phoneRegex.test(phoneNumber);
  };
  
  // 생년월일 유효성 검사 함수
  const validateBirthdate = (birthdate) => {
    // YYYY.MM.DD 형식인지 확인
    const birthdateRegex = /^\d{4}\.\d{2}\.\d{2}$/;
    if (!birthdateRegex.test(birthdate)) return false;
    
    // 날짜 유효성 검사
    const parts = birthdate.split('.');
    const year = parseInt(parts[0], 10);
    const month = parseInt(parts[1], 10);
    const day = parseInt(parts[2], 10);
    
    // 유효한 범위 확인
    if (year < 1900 || year > new Date().getFullYear()) return false;
    if (month < 1 || month > 12) return false;
    
    // 각 월의 일수 확인
    const daysInMonth = new Date(year, month, 0).getDate();
    if (day < 1 || day > daysInMonth) return false;
    
    return true;
  };
  
  // 생년월일 형식화 함수
  const formatBirthdate = (value) => {
    // 숫자만 추출
    const numbers = value.replace(/\D/g, '');
    
    if (numbers.length <= 4) {
      return numbers;
    } else if (numbers.length <= 6) {
      return `${numbers.slice(0, 4)}.${numbers.slice(4)}`;
    } else {
      return `${numbers.slice(0, 4)}.${numbers.slice(4, 6)}.${numbers.slice(6, 8)}`;
    }
  };

  // 비밀번호 변경 시 확인
  useEffect(() => {
    if (formData.password) {
      // 비밀번호 유효성 검사
      if (!validatePassword(formData.password)) {
        setValidationErrors(prev => ({
          ...prev,
          password: '비밀번호는 특수문자와 숫자를 포함한 8자리 이상이어야 합니다.'
        }));
      } else {
        setValidationErrors(prev => ({
          ...prev,
          password: ''
        }));
      }
    }
    
    // 비밀번호 확인 필드가 있을 경우 일치 여부 확인
    if (formData.passwordConfirm) {
      if (formData.password !== formData.passwordConfirm) {
        setValidationErrors(prev => ({
          ...prev,
          passwordConfirm: '비밀번호가 일치하지 않습니다.'
        }));
      } else {
        setValidationErrors(prev => ({
          ...prev,
          passwordConfirm: ''
        }));
      }
    }
  }, [formData.password, formData.passwordConfirm]);
  
  // 입력 필드 변경 핸들러
  const handleChange = (e) => {
    const { name, value } = e.target;
    
    if (name === 'birthdate') {
      // 생년월일 필드의 경우 형식화 적용
      const formattedValue = formatBirthdate(value);
      setFormData({
        ...formData,
        [name]: formattedValue,
      });
      
      // 생년월일 유효성 검사
      if (formattedValue.length === 10) { // YYYY.MM.DD 형식 완성 시
        if (!validateBirthdate(formattedValue)) {
          setValidationErrors(prev => ({
            ...prev,
            birthdate: '유효하지 않은 생년월일입니다.'
          }));
        } else {
          setValidationErrors(prev => ({
            ...prev,
            birthdate: ''
          }));
        }
      }
    } else if (name === 'phoneNumber') {
      // 숫자만 입력 가능하고 11자리로 제한
      const numbersOnly = value.replace(/\D/g, '');
      if (numbersOnly.length <= 11) {
        setFormData({
          ...formData,
          [name]: numbersOnly,
        });
        
        // 전화번호 유효성 검사
        if (numbersOnly.length === 11) {
          if (!validatePhoneNumber(numbersOnly)) {
            setValidationErrors(prev => ({
              ...prev,
              phoneNumber: '유효하지 않은 전화번호입니다.'
            }));
          } else {
            setValidationErrors(prev => ({
              ...prev,
              phoneNumber: ''
            }));
          }
        }
      }
    } else {
      setFormData({
        ...formData,
        [name]: value,
      });
    }
  };
  
  // 전화번호 인증 버튼 클릭 핸들러
  const handleVerifyPhoneClick = async () => {
    if (!formData.phoneNumber) {
      setDialogMessage('전화번호를 입력해주세요.');
      setShowDialog(true);
      return;
    }
    
    // 전화번호 유효성 검사
    if (!validatePhoneNumber(formData.phoneNumber)) {
      setDialogMessage('전화번호는 숫자 11자리여야 합니다.');
      setShowDialog(true);
      return;
    }
    
    try {
      setIsLoading(true);
      
      // API 호출하여 인증번호 발송
      await sendVerificationSMS(formData.phoneNumber);
      
      setIsVerifySent(true);
      setDialogMessage('인증번호가 발송되었습니다.');
      setShowDialog(true);
    } catch (error) {
      console.error('인증번호 발송 실패:', error);
      setDialogMessage(error.message || '인증번호 발송에 실패했습니다.');
      setShowDialog(true);
    } finally {
      setIsLoading(false);
    }
  };
  
  // 인증번호 확인 버튼 클릭 핸들러
  const handleVerifyCodeClick = async () => {
    if (!formData.verificationCode) {
      setDialogMessage('인증번호를 입력해주세요.');
      setShowDialog(true);
      return;
    }
    
    try {
      setIsLoading(true);
      
      // API 호출하여 인증번호 검증
      const response = await verifyVerificationCode(formData.phoneNumber, formData.verificationCode);
      
      setIsVerified(true);
      setDialogMessage(response.message || '전화번호 인증이 완료되었습니다.');
      setShowDialog(true);
      
      // 폼 데이터에서 인증 코드 필드 초기화
      setFormData({
        ...formData,
        verificationCode: ''
      });
      
    } catch (error) {
      console.error('인증번호 확인 실패:', error);
      setDialogMessage(error.message || '인증번호 확인에 실패했습니다. 다시 시도해주세요.');
      setShowDialog(true);
    } finally {
      setIsLoading(false);
    }
  };
  
  // 약관 동의 체크박스 핸들러
  const handleAgreementChange = (e) => {
    const { name, checked } = e.target;
    
    if (name === 'all') {
      // '전체 동의' 체크박스가 변경된 경우 모든 체크박스 상태 업데이트
      setAgreements({
        all: checked,
        service: checked,
        privacy: checked
      });
    } else {
      // 개별 체크박스가 변경된 경우
      const newAgreements = {
        ...agreements,
        [name]: checked
      };
      
      // 모든 필수 약관이 체크되었는지 확인하여 '전체 동의' 업데이트
      newAgreements.all = newAgreements.service && newAgreements.privacy;
      
      setAgreements(newAgreements);
    }
  };
  
  // 폼 제출 핸들러
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // 비밀번호 유효성 검사
    if (!validatePassword(formData.password)) {
      setDialogMessage('비밀번호는 특수문자와 숫자를 포함한 8자리 이상이어야 합니다.');
      setShowDialog(true);
      return;
    }
    
    // 비밀번호 일치 검사
    if (formData.password !== formData.passwordConfirm) {
      setDialogMessage('비밀번호가 일치하지 않습니다.');
      setShowDialog(true);
      return;
    }
    
    // 전화번호 유효성 검사
    if (!validatePhoneNumber(formData.phoneNumber)) {
      setDialogMessage('전화번호는 숫자 11자리여야 합니다.');
      setShowDialog(true);
      return;
    }
    
    // 생년월일 유효성 검사
    if (formData.birthdate && !validateBirthdate(formData.birthdate)) {
      setDialogMessage('유효하지 않은 생년월일입니다.');
      setShowDialog(true);
      return;
    }
    
    // 필수 필드 검사
    const requiredFields = [
      { field: 'email', label: '이메일' },
      { field: 'name', label: '이름' },
      { field: 'password', label: '비밀번호' },
      { field: 'phoneNumber', label: '전화번호' },
      { field: 'birthdate', label: '생년월일' }
    ];
    
    for (const { field, label } of requiredFields) {
      if (!formData[field]) {
        setDialogMessage(`${label}을(를) 입력해주세요.`);
        setShowDialog(true);
        return;
      }
    }
    
    // 전화번호 인증 검사
    if (!isVerified) {
      setDialogMessage('전화번호 인증을 완료해주세요.');
      setShowDialog(true);
      return;
    }
    
    // 약관 동의 검사
    if (!agreements.service || !agreements.privacy) {
      setDialogMessage('필수 약관에 동의해주세요.');
      setShowDialog(true);
      return;
    }
    
    try {
      setIsLoading(true);
      
      // 회사 코드로 회사 정보 조회
      const companyCode = formData.companyCode.trim();
      let companyName = ""; // 기본값
      let wage = "0"; // 기본값
      
      // 회사 코드가 비어있지 않은 경우에만 API 호출
      if (companyCode) {
        try {
          const companyResponse = await getPublicCompanyInfo(companyCode);
          
          if (companyResponse.success && companyResponse.data) {
            companyName = companyResponse.data.company_name || "";
            console.log('회사 정보 조회 결과:', companyName);
          } else {
            // 회사 정보 조회 실패 처리
            setIsLoading(false);
            setDialogMessage('유효하지 않은 회사 코드입니다.');
            setShowDialog(true);
            return; // 회원가입 중단
          }
          
          // 회사 코드가 있는 경우에만 계약 정보 조회
          try {
            const contractResponse = await getCompanyContract(companyCode);
            if (contractResponse.success && contractResponse.data) {
              wage = contractResponse.data.wage || "0";
              console.log('계약 정보 조회 결과:', wage);
            } else {
              // 계약 정보 조회 실패 처리
              wage = "0"; // 기본값
            }
          } catch (contractError) {
            console.error('계약 정보 조회 중 오류:', contractError);
            wage = "0"; // 오류 발생 시 기본값
          }
        } catch (companyError) {
          console.error('회사 정보 조회 중 오류:', companyError);
          // 오류 발생 시에도 빈 문자열로 계속 진행
        }
      }
      
      // 회원가입 데이터 구성
      const userData = {
        email: formData.email.trim(),
        name: formData.name.trim(),
        password: formData.password,
        phone: formData.phoneNumber.trim(),
        companyCode: companyCode,
        termsAgreed: true,
        birth: formData.birthdate.trim(),
        profileImage: "",  // 빈 문자열로 초기화
        companyName: companyName,  // API에서 가져온 회사 이름 사용
        userType: "common", // 일반 사용자 타입 설정
        wage: wage,
      };
      
      console.log('회원가입 요청 데이터:', userData);
      
      // 회원가입 API 호출
      const response = await registerUserCommon(userData);
      
      if (response.success) {
        // 회원가입 성공 처리
        setRegistrationSuccess(true);
        setDialogMessage('회원가입이 완료되었습니다. 앱에서 로그인을 진행하세요.');
        setShowDialog(true);
        // 성공 시 추가 처리는 다이얼로그 닫을 때 처리
      } else {
        // 오류 메시지 표시
        setDialogMessage(response.message || '회원가입에 실패했습니다.');
        setShowDialog(true);
      }
    } catch (error) {
      console.error('회원가입 실패:', error);
      
      // 오류 메시지 표시
      setDialogMessage(error.message || '회원가입 중 오류가 발생했습니다.');
      setShowDialog(true);
    } finally {
      setIsLoading(false);
    }
  };
  
  // 다이얼로그 닫기 핸들러
  const handleDialogClose = () => {
    setShowDialog(false);
    
    // 회원가입이 완료되었을 때만 로그인 페이지로 이동
    if (dialogMessage === '회원가입이 완료되었습니다. 앱에서 로그인을 진행하세요.') {
      navigate('/login', { replace: true }); // replace: true를 사용하여 뒤로가기 방지
    }
  };
  
  // 뒤로가기 방지를 위한 useEffect
  useEffect(() => {
    if (registrationSuccess) {
      // 브라우저 히스토리 뒤로가기 방지
      window.history.pushState(null, "", window.location.pathname);
      window.addEventListener("popstate", blockNavigation);
      
      return () => {
        window.removeEventListener("popstate", blockNavigation);
      };
    }
  }, [registrationSuccess]);
  
  // 뒤로가기 차단 함수
  const blockNavigation = (e) => {
    window.history.pushState(null, "", window.location.pathname);
    setDialogMessage("유효하지 않은 접근입니다. 로그인 페이지로 이동하세요.");
    setShowDialog(true);
  };
  
  // 로그인 페이지로 이동하는 함수
  const goToLoginPage = () => {
    navigate('/login', { replace: true });
  };
  
  // 회원가입 성공 화면 렌더링
  if (registrationSuccess) {
    return (
      <>
        <div className="appBar" style={{ backgroundColor: COLORS.appBar }}>
          <div className="appBarLogo">
            <img src={logoImage} alt="TapGo Logo" className="appBarLogoImage" />
          </div>
        </div>
        
        <div className="registerContainer">
          <div className="registrationSuccessContainer">
            <div className="successIcon">✓</div>
            <h2 className="successTitle">회원가입이 완료되었습니다</h2>
            <p className="successMessage">앱에서 로그인을 진행하세요</p>
          </div>
        </div>
        
        {/* 다이얼로그 */}
        {showDialog && (
          <Dialog message={dialogMessage} onClose={handleDialogClose} />
        )}
      </>
    );
  }

  return (
    <>
      <div className="appBar" style={{ backgroundColor: COLORS.appBar }}>
        <div className="appBarLogo">
          <img src={logoImage} alt="TapGo Logo" className="appBarLogoImage" />
        </div>
      </div>
      
      <div className="registerContainer">
        <h2 className="registerTitle">사용자 회원가입</h2>
        
        <form onSubmit={handleSubmit} className="registerForm">
          {/* 이메일 */}
          <div className="formGroup">
            <label className="label">이메일</label>
            <input
              type="email"
              className="input"
              name="email"
              value={formData.email}
              onChange={handleChange}
              placeholder="이메일"
              required
            />
          </div>
          
          {/* 이름 */}
          <div className="formGroup">
            <label className="label">이름</label>
            <input
              type="text"
              className="input"
              name="name"
              value={formData.name}
              onChange={handleChange}
              placeholder="이름"
              required
            />
          </div>
          
          {/* 비밀번호 */}
          <div className="formRow">
            <div className="formGroup">
              <label className="label">비밀번호</label>
              <input
                type="password"
                className="input"
                name="password"
                value={formData.password}
                onChange={handleChange}
                placeholder="비밀번호 (특수문자, 숫자 포함 8자 이상)"
                required
              />
              {validationErrors.password && (
                <p className="validationError">{validationErrors.password}</p>
              )}
            </div>
            <div className="formGroup">
              <label className="label">비밀번호 확인</label>
              <input
                type="password"
                className="input"
                name="passwordConfirm"
                value={formData.passwordConfirm}
                onChange={handleChange}
                placeholder="비밀번호 확인"
                required
              />
              {validationErrors.passwordConfirm && (
                <p className="validationError">{validationErrors.passwordConfirm}</p>
              )}
            </div>
          </div>
          
          {/* 전화번호 인증 */}
          <div className="formRow">
            <div className="formGroup">
              <label className="label">전화번호</label>
              <input
                type="tel"
                className="input"
                name="phoneNumber"
                value={formData.phoneNumber}
                onChange={handleChange}
                placeholder="전화번호 (- 없이 11자리 입력)"
                required
                disabled={isVerified}
                maxLength="11"
              />
              {validationErrors.phoneNumber && (
                <p className="validationError">{validationErrors.phoneNumber}</p>
              )}
            </div>
            <div className="formGroup" style={{ display: 'flex', alignItems: 'flex-end' }}>
              <button
                type="button"
                className="registerButton"
                onClick={handleVerifyPhoneClick}
                disabled={isVerified || !formData.phoneNumber || formData.phoneNumber.length !== 11 || isLoading}
                style={{ 
                  marginBottom: '0',
                  backgroundColor: isVerified ? '#4CAF50' : undefined // 인증 완료 시 초록색으로 변경
                }}
              >
                {isLoading ? '처리 중...' : isVerified ? '인증완료' : isVerifySent ? '재발송' : '인증번호 발송'}
              </button>
            </div>
          </div>
          
          {/* 인증번호 입력 (인증번호 발송 후에만 표시) */}
          {isVerifySent && !isVerified && (
            <div className="formRow">
              <div className="formGroup">
                <label className="label">인증번호</label>
                <input
                  type="text"
                  className="input"
                  name="verificationCode"
                  value={formData.verificationCode}
                  onChange={handleChange}
                  placeholder="인증번호 입력"
                  required
                />
              </div>
              <div className="formGroup" style={{ display: 'flex', alignItems: 'flex-end' }}>
                <button
                  type="button"
                  className="registerButton"
                  onClick={handleVerifyCodeClick}
                  disabled={!formData.verificationCode || isLoading}
                  style={{ marginBottom: '0' }}
                >
                  {isLoading ? '확인 중...' : '확인'}
                </button>
              </div>
            </div>
          )}
          
          {/* 회사코드 */}
          <div className="formGroup">
            <label className="label">회사코드</label>
            <input
              type="text"
              className="input"
              name="companyCode"
              value={formData.companyCode}
              onChange={handleChange}
              placeholder="회사코드"
              required
            />
          </div>
          
          {/* 생년월일 */}
          <div className="formGroup">
            <label className="label">생년월일</label>
            <input
              type="text"
              className="input"
              name="birthdate"
              value={formData.birthdate}
              onChange={handleChange}
              placeholder="YYYY.MM.DD"
              maxLength="10"
              required
            />
            {validationErrors.birthdate && (
              <p className="validationError">{validationErrors.birthdate}</p>
            )}
          </div>
          
          {/* 약관 동의 */}
          <div className="formGroup" style={{ marginTop: '20px' }}>
            <div className="checkboxGroup">
              <label className="checkboxLabel">
                <input
                  type="checkbox"
                  name="all"
                  checked={agreements.all}
                  onChange={handleAgreementChange}
                />
                <span className="checkboxText bold">약관 전체 동의</span>
              </label>
            </div>
            
            <div className="checkboxGroup" style={{ marginLeft: '20px', marginTop: '10px' }}>
              <label className="checkboxLabel">
                <input
                  type="checkbox"
                  name="service"
                  checked={agreements.service}
                  onChange={handleAgreementChange}
                />
                <span className="checkboxText">서비스 이용약관 동의 (필수)</span>
              </label>
              <a href="https://www.notion.so/1cffbd3a2da380de85a0d27d6ef2277e" className="termsLink" target="_blank" rel="noopener noreferrer">보기</a>
            </div>
            
            <div className="checkboxGroup" style={{ marginLeft: '20px', marginTop: '10px' }}>
              <label className="checkboxLabel">
                <input
                  type="checkbox"
                  name="privacy"
                  checked={agreements.privacy}
                  onChange={handleAgreementChange}
                />
                <span className="checkboxText">개인정보 처리방침 동의 (필수)</span>
              </label>
              <a href="https://www.notion.so/1cffbd3a2da3806fa0d7d6c3a3c79fb8" className="termsLink" target="_blank" rel="noopener noreferrer">보기</a>
            </div>
          </div>
          
          {/* 회원가입 버튼 */}
          <div style={{ position: 'relative' }}>
            <button 
              type="submit" 
              className="registerButton"
              style={{ marginTop: '40px', marginBottom: '40px' }}
              disabled={isLoading || !isVerified}
              title={!isVerified ? "전화번호 인증이 필요합니다" : ""}
            >
              {isLoading ? '처리 중...' : '회원가입'}
            </button>
            
            {!isVerified && (
              <div className="verificationRequired">
                전화번호 인증을 완료해야 회원가입이 가능합니다
              </div>
            )}
          </div>
          
        </form>
      </div>
      
      {/* 다이얼로그 */}
      {showDialog && (
        <Dialog message={dialogMessage} onClose={handleDialogClose} />
      )}
    </>
  );
};

export default UserRegister; 